import { type ReactNode, createContext, useContext, useEffect, useMemo, useState } from 'react';
import type { User } from '@supabase/supabase-js';
import { catalogProducts } from '../data/catalog';
import { fetchProfile } from '../services/account';
import { fetchCategories, fetchPublishedProducts, productFromCart } from '../services/products';
import { isSupabaseConfigured, supabase } from '../services/supabase';
import type { CartItem, Category, Product, Profile } from '../types';

const CART_KEY = 'sena-beachway-cart';
const FAVORITES_KEY = 'sena-beachway-favorites';

function readStorage<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

type CommerceContextValue = {
  products: Product[];
  categories: Category[];
  isCatalogLoading: boolean;
  refreshCatalog: () => Promise<void>;
  cart: CartItem[];
  cartOpen: boolean;
  setCartOpen: (open: boolean) => void;
  addToCart: (product: Product, color: string, size: string, quantity?: number) => void;
  updateQuantity: (itemId: string, quantity: number) => void;
  removeFromCart: (itemId: string) => void;
  clearCart: () => void;
  cartCount: number;
  cartSubtotal: number;
  cartHasConsultation: boolean;
  favorites: string[];
  toggleFavorite: (productId: string) => void;
  isFavorite: (productId: string) => boolean;
  user: User | null;
  profile: Profile | null;
  authReady: boolean;
  signIn: (email: string, password: string) => Promise<string | null>;
  signUp: (name: string, phone: string, email: string, password: string) => Promise<string | null>;
  signOut: () => Promise<void>;
};

const CommerceContext = createContext<CommerceContextValue | null>(null);

export function CommerceProvider({ children }: { children: ReactNode }) {
  const [products, setProducts] = useState<Product[]>(catalogProducts);
  const [categories, setCategories] = useState<Category[]>([]);
  const [isCatalogLoading, setIsCatalogLoading] = useState(true);
  const [cart, setCart] = useState<CartItem[]>(() => readStorage<CartItem[]>(CART_KEY, []));
  const [favorites, setFavorites] = useState<string[]>(() => readStorage<string[]>(FAVORITES_KEY, []));
  const [cartOpen, setCartOpen] = useState(false);
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [authReady, setAuthReady] = useState(!isSupabaseConfigured);

  const refreshCatalog = async () => {
    setIsCatalogLoading(true);
    const [nextProducts, nextCategories] = await Promise.all([fetchPublishedProducts(), fetchCategories()]);
    setProducts(nextProducts);
    setCategories(nextCategories);
    setIsCatalogLoading(false);
  };

  useEffect(() => {
    void refreshCatalog();
  }, []);

  useEffect(() => {
    localStorage.setItem(CART_KEY, JSON.stringify(cart));
  }, [cart]);

  useEffect(() => {
    localStorage.setItem(FAVORITES_KEY, JSON.stringify(favorites));
  }, [favorites]);

  useEffect(() => {
    const client = supabase;
    if (!client) return undefined;
    let active = true;
    const bootstrap = async () => {
      const { data } = await client.auth.getSession();
      if (!active) return;
      setUser(data.session?.user ?? null);
      setAuthReady(true);
    };
    void bootstrap();
    const { data: listener } = client.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
      setAuthReady(true);
    });
    return () => {
      active = false;
      listener.subscription.unsubscribe();
    };
  }, []);

  useEffect(() => {
    if (!user) {
      setProfile(null);
      return;
    }
    void fetchProfile(user.id).then(setProfile);
    if (!supabase) return;
    void supabase
      .from('favorites')
      .select('product_id')
      .eq('user_id', user.id)
      .then(({ data }) => {
        if (!data) return;
        setFavorites((current) => [...new Set([...current, ...data.map((row) => String(row.product_id))])]);
      });
  }, [user]);

  const addToCart = (product: Product, color: string, size: string, quantity = 1) => {
    const id = `${product.id}:${color}:${size}`;
    setCart((current) => {
      const existing = current.find((item) => item.id === id);
      if (!existing) return [...current, { id, productId: product.id, color, size, quantity }];
      return current.map((item) => item.id === id ? { ...item, quantity: item.quantity + quantity } : item);
    });
    setCartOpen(true);
  };

  const updateQuantity = (itemId: string, quantity: number) => {
    setCart((current) => quantity < 1
      ? current.filter((item) => item.id !== itemId)
      : current.map((item) => item.id === itemId ? { ...item, quantity } : item));
  };

  const removeFromCart = (itemId: string) => setCart((current) => current.filter((item) => item.id !== itemId));
  const clearCart = () => setCart([]);

  const toggleFavorite = (productId: string) => {
    const shouldAdd = !favorites.includes(productId);
    setFavorites((current) => shouldAdd ? [...current, productId] : current.filter((id) => id !== productId));
    if (!user || !supabase) return;
    if (shouldAdd) {
      void supabase.from('favorites').upsert({ user_id: user.id, product_id: productId }, { onConflict: 'user_id,product_id' });
    } else {
      void supabase.from('favorites').delete().eq('user_id', user.id).eq('product_id', productId);
    }
  };

  const signIn = async (email: string, password: string) => {
    if (!supabase) return 'Configure o Supabase para habilitar o login.';
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return error?.message ?? null;
  };

  const signUp = async (name: string, phone: string, email: string, password: string) => {
    if (!supabase) return 'Configure o Supabase para habilitar o cadastro.';
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: { data: { name, phone } },
    });
    return error?.message ?? null;
  };

  const signOut = async () => {
    if (supabase) await supabase.auth.signOut();
  };

  const cartLines = useMemo(() => cart.map((item) => ({ item, product: productFromCart(item.productId, products) })).filter(
    (line): line is { item: CartItem; product: Product } => Boolean(line.product),
  ), [cart, products]);
  const cartSubtotal = cartLines.reduce((total, line) => total + (line.product.promotionalPrice ?? line.product.price ?? 0) * line.item.quantity, 0);
  const cartHasConsultation = cartLines.some((line) => line.product.price === null && line.product.promotionalPrice === null);

  const value: CommerceContextValue = {
    products,
    categories,
    isCatalogLoading,
    refreshCatalog,
    cart,
    cartOpen,
    setCartOpen,
    addToCart,
    updateQuantity,
    removeFromCart,
    clearCart,
    cartCount: cart.reduce((total, item) => total + item.quantity, 0),
    cartSubtotal,
    cartHasConsultation,
    favorites,
    toggleFavorite,
    isFavorite: (productId) => favorites.includes(productId),
    user,
    profile,
    authReady,
    signIn,
    signUp,
    signOut,
  };

  return <CommerceContext.Provider value={value}>{children}</CommerceContext.Provider>;
}

export function useCommerce() {
  const context = useContext(CommerceContext);
  if (!context) throw new Error('useCommerce must be used inside CommerceProvider.');
  return context;
}
